import { Box, Button, Chip, IconButton, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import AgenticPanelToolbar from 'components/@home/@settings/Tabs/Agents/panels/AgenticPanelToolbar';
import AttachSecretsDialog from 'components/@home/@settings/Tabs/Agents/toolPanels/AttachSecretsDialog';
import CreateSecretDialog from 'components/@home/@settings/Tabs/Agents/toolPanels/CreateSecretDialog';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import KeyVariantIcon from 'mdi-react/KeyVariantIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { func, object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Secret from 'services/api/Secret';
import { I18n } from 'utils/i18n';

const SecretsPanel = ({ tool, setTool }) => {
  const theme = useTheme();
  const mutedChip = semanticTone(theme, 'default');
  const typeChipSx = {
    backgroundColor: mutedChip.bg,
    color: mutedChip.fg,
    fontSize: theme.typography.pxToRem(11),
    height: 22,
    '& .MuiChip-label': { padding: '0 8px' },
    alignSelf: 'flex-start',
  };

  const [allSecrets, setAllSecrets] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    Secret.list()
      .then(setAllSecrets)
      .catch(() => setAllSecrets([]));
  }, []);

  const attachedNames = useMemo(() => tool.secretNames || [], [tool.secretNames]);

  const rows = useMemo(
    () =>
      attachedNames
        .map(name => allSecrets.find(s => s.name === name))
        .filter(Boolean)
        .map(secret => ({ ...secret, id: secret._id })),
    [attachedNames, allSecrets],
  );

  const detach = name => {
    setTool({
      ...tool,
      secretNames: (tool.secretNames || []).filter(n => n !== name),
    });
  };

  const handleSaveAttached = nextNames => {
    setTool({ ...tool, secretNames: nextNames });
  };

  const handleCreateSecret = async payload => {
    const created = await Secret.create({
      ...payload,
      source: { kind: 'agent_tool', toolId: tool._id },
    });
    setAllSecrets(prev => [...prev.filter(s => s.name !== created.name), created]);
    if (!(tool.secretNames || []).includes(created.name)) {
      setTool({ ...tool, secretNames: [...(tool.secretNames || []), created.name] });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: I18n.t('AgenticChatFlow.SecretName'),
        flex: 1.4,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
          >
            <KeyVariantIcon size={16} />
            {row.name}
          </Box>
        ),
      },
      {
        field: 'type',
        headerName: I18n.t('AgenticChatFlow.SecretType'),
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={I18n.t(`AgenticChatFlow.SecretType_${row.type}`)}
            sx={typeChipSx}
          />
        ),
      },
      {
        field: 'description',
        headerName: I18n.t('AgenticChatFlow.Description'),
        flex: 2,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Box component="span" sx={{ color: 'text.secondary' }} title={row.description || ''}>
            {row.description || '—'}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        flex: 0.4,
        minWidth: 80,
        renderCell: ({ row }) => (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              detach(row.name);
            }}
            sx={{ alignSelf: 'flex-start' }}
            aria-label={I18n.t('AgenticChatFlow.Detach')}
          >
            <TrashCanOutlineIcon size={16} />
          </IconButton>
        ),
      },
    ],
    [typeChipSx], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toolbarActions = (
    <>
      <Button variant="outlined" color="secondary" onClick={() => setManageOpen(true)}>
        <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
          <CogOutlineIcon />
        </SvgIcon>
        {I18n.t('AgenticChatFlow.ManageSecrets')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        disableElevation
        onClick={() => setCreateOpen(true)}
      >
        <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'inherit !important' }}>
          <PlusIcon />
        </SvgIcon>
        {I18n.t('AgenticChatFlow.CreateSecret')}
      </Button>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
      <OmmnioDataGrid
        rows={rows}
        columns={columns}
        getRowId={r => r._id}
        rowCount={rows.length}
        autoHeight={false}
        disableEvenOdd
        defaultSortModel={[{ field: 'name', sort: 'asc' }]}
        slotProps={{
          toolbar: {
            searchPlaceholder: I18n.t('AgenticChatFlow.SearchSecrets'),
            children: toolbarActions,
          },
        }}
        slots={{ toolbar: AgenticPanelToolbar }}
        sx={{ flex: 1, minHeight: 280, borderRadius: 1 }}
      />

      <AttachSecretsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        secrets={allSecrets}
        selectedNames={attachedNames}
        onSave={handleSaveAttached}
        onCreate={() => {
          setManageOpen(false);
          setCreateOpen(true);
        }}
      />

      {createOpen && (
        <CreateSecretDialog
          open
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreateSecret}
        />
      )}
    </Box>
  );
};

SecretsPanel.propTypes = {
  tool: object.isRequired,
  setTool: func.isRequired,
};

export default SecretsPanel;
